import React, { useEffect, useRef, useState } from 'react';

function PictoFace({ face }) {
  const color = face.color || '#f76f4b';
  const common = { fill: color };
  return (
    <svg viewBox="0 0 64 64" className="w-[62%] h-[62%]" aria-hidden>
      {face.shape === 'circle' && <circle cx="32" cy="32" r="20" {...common} />}
      {face.shape === 'square' && <rect x="12" y="12" width="40" height="40" rx="6" {...common} />}
      {face.shape === 'triangle' && <polygon points="32,10 54,52 10,52" {...common} />}
      {face.shape === 'diamond' && <polygon points="32,8 56,32 32,56 8,32" {...common} />}
      {face.shape === 'star' && <polygon points="32,8 39,24 56,26 43,38 47,54 32,45 17,54 21,38 8,26 25,24" {...common} />}
      {face.shape === 'hex' && <polygon points="32,8 52,19 52,45 32,56 12,45 12,19" {...common} />}
      {face.shape === 'heart' && <path d="M32 54C18 42 8 32 8 22a12 12 0 0 1 24 0 12 12 0 0 1 24 0c0 10-10 20-24 32z" {...common} />}
      {face.shape === 'plus' && <path d="M26 10h12v16h16v12H38v16H26V38H10V26h16z" {...common} />}
      {face.shape === 'moon' && <path d="M40 10a22 22 0 1 0 10 38 20 20 0 1 1-10-38z" {...common} />}
      {face.shape === 'leaf' && <path d="M48 12C28 14 14 28 14 46c18 0 32-14 34-34z" {...common} />}
      {face.shape === 'ring' && <circle cx="32" cy="32" r="18" fill="none" stroke={color} strokeWidth="8" />}
      {face.shape === 'bolt' && <polygon points="36,8 18,36 30,36 26,56 48,26 34,26" {...common} />}
    </svg>
  );
}

function CardFace({ face }) {
  if (face?.type === 'photo' && face.src) {
    return <img src={face.src} alt="" className="w-full h-full object-cover" draggable={false} />;
  }
  return <PictoFace face={face || {}} />;
}

export default function MemoryBoard({ payload, savedMatched, savedMoves = 0, compact = false, onSolved, onProgress }) {
  const cards = payload?.cards || [];
  const cols = payload?.cols || 4;
  const pairCount = cards.length / 2;
  const [matched, setMatched] = useState(() => [...(savedMatched || [])]);
  const [moves, setMoves] = useState(() => Math.max(0, Number(savedMoves) || 0));
  const [flipped, setFlipped] = useState([]);
  const matchedRef = useRef([...(savedMatched || [])]);
  const movesRef = useRef(Math.max(0, Number(savedMoves) || 0));
  const flippedRef = useRef([]);
  const lockRef = useRef(false);
  const solvedRef = useRef(false);

  useEffect(() => {
    matchedRef.current = [...(savedMatched || [])];
    movesRef.current = Math.max(0, Number(savedMoves) || 0);
    setMatched(matchedRef.current);
    setMoves(movesRef.current);
    setFlipped([]);
    flippedRef.current = [];
    lockRef.current = false;
    solvedRef.current = false;
    const pairs = new Set(cards.map((c) => c.pair));
    if (pairs.size && pairs.size === matchedRef.current.length && !solvedRef.current) {
      solvedRef.current = true;
      onSolved?.();
    }
  }, [payload]);

  const finishIfDone = (nextMatched) => {
    if (nextMatched.length >= pairCount && !solvedRef.current) {
      solvedRef.current = true;
      onSolved?.();
    }
  };

  const flip = (id) => {
    if (lockRef.current || solvedRef.current) return;
    const card = cards.find((c) => c.id === id);
    if (!card || matchedRef.current.includes(card.pair) || flippedRef.current.includes(id)) return;
    const nextFlipped = [...flippedRef.current, id];
    flippedRef.current = nextFlipped;
    setFlipped(nextFlipped);
    if (nextFlipped.length < 2) return;
    const nextMoves = movesRef.current + 1;
    movesRef.current = nextMoves;
    setMoves(nextMoves);
    const [a, b] = nextFlipped.map((fid) => cards.find((c) => c.id === fid));
    if (a.pair === b.pair) {
      const nextMatched = [...matchedRef.current, a.pair];
      matchedRef.current = nextMatched;
      setMatched(nextMatched);
      flippedRef.current = [];
      setFlipped([]);
      onProgress?.({ matched: nextMatched, moves: nextMoves });
      finishIfDone(nextMatched);
      return;
    }
    onProgress?.({ matched: matchedRef.current, moves: nextMoves });
    lockRef.current = true;
    setTimeout(() => {
      flippedRef.current = [];
      setFlipped([]);
      lockRef.current = false;
    }, 750);
  };

  const open = new Set([...flipped, ...cards.filter((c) => matched.includes(c.pair)).map((c) => c.id)]);
  const wrapRef = useRef(null);
  const [pane, setPane] = useState({ w: 0, h: 0 });
  const rows = payload?.rows || Math.max(1, Math.ceil(cards.length / Math.max(1, cols)));

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const update = () => {
      const dialog = el.closest('[data-game-season-dialog]');
      const scroller = dialog?.querySelector('.overflow-y-auto') || el.closest('.overflow-y-auto');
      const bottom = scroller
        ? scroller.getBoundingClientRect().bottom
        : window.innerHeight - 16;
      const top = el.getBoundingClientRect().top;
      setPane({
        w: Math.max(160, el.clientWidth),
        h: Math.max(140, bottom - top - 16),
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    const dialog = el.closest('[data-game-season-dialog]');
    const scroller = dialog?.querySelector('.overflow-y-auto') || el.closest('.overflow-y-auto');
    if (scroller) ro.observe(scroller);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [payload, compact, cards.length, cols]);

  const gap = compact ? 4 : (cols >= 6 ? 6 : (rows >= 6 ? 6 : 8));
  const statsH = 22;
  const availH = Math.max(120, pane.h - statsH);
  const cardFromW = pane.w ? (pane.w - gap * (cols - 1)) / cols : 80;
  const cardFromH = (availH - gap * (rows - 1)) / rows;
  const cap = compact ? 96 : 220;
  const cardPx = Math.max(40, Math.floor(Math.min(cardFromW, cardFromH, cap)));
  const gridW = cardPx * cols + gap * (cols - 1);
  const qSize = Math.max(14, Math.round(cardPx * 0.32));

  return (
    <div ref={wrapRef} className="w-full min-w-0">
      <p className="text-xs text-gray-500 mb-1.5">
        {matched.length} / {pairCount} paires
      </p>
      <div
        className="grid mx-auto"
        style={{
          gridTemplateColumns: `repeat(${cols}, ${cardPx}px)`,
          width: gridW,
          gap,
        }}
      >
        {cards.map((card) => {
          const isOpen = open.has(card.id);
          const isMatch = matched.includes(card.pair);
          return (
            <button
              key={card.id}
              type="button"
              onClick={() => flip(card.id)}
              className={`rounded-xl overflow-hidden border-2 flex items-center justify-center transition-colors ${
                isMatch
                  ? 'border-emerald-400 bg-white'
                  : isOpen
                    ? 'border-[#f76f4b] bg-white'
                    : 'border-[#f76f4b]/40 bg-[#111827]'
              }`}
              style={{ width: cardPx, height: cardPx }}
            >
              {isOpen ? (
                <CardFace face={card.face} />
              ) : (
                <span
                  className="font-black text-[#f76f4b]/90 select-none leading-none"
                  style={{ fontSize: qSize }}
                >
                  ?
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
